import type { DataTypeName, RuleConfig, RuleEntry, RuleSeverity, RuleType } from '../../types/fileUploadRules'

export const RULE_TYPES: RuleType[] = ['RequiredNonNull', 'DataType', 'Range', 'Regex', 'AllowedValues']

export const SEVERITIES: RuleSeverity[] = ['Error', 'Warning']

export const DATA_TYPES: DataTypeName[] = ['date', 'decimal', 'int', 'guid', 'bool']

export const RULE_TYPE_HINTS: Record<RuleType, string> = {
  RequiredNonNull: 'The column must be present and non-empty on every row. Optionally remove rows where it is empty or NULL.',
  DataType: 'Every non-empty value must parse as the chosen data type. The selection also defines the uploaded column type when values are empty or ambiguous.',
  Range: 'Constrain values to a numeric range, or text to a length range.',
  Regex: 'Every value must match the regular expression.',
  AllowedValues: 'Every value must be one of the allowed values.',
}

export type RangeMode = 'numeric' | 'text'

export function getRangeMode(config?: RuleConfig): RangeMode {
  if (config && (config.minLength != null || config.maxLength != null)) return 'text'
  return 'numeric'
}

// Keep only the config keys relevant to the rule type; omit config entirely
// when RequiredNonNull has no optional behavior enabled.
export function sanitizeConfig(ruleType: RuleType, config?: RuleConfig): RuleConfig | undefined {
  if (!config) return ruleType === 'RequiredNonNull' ? undefined : {}

  switch (ruleType) {
    case 'RequiredNonNull':
      return config.removeNullRows ? { removeNullRows: true } : undefined
    case 'DataType':
      return config.type ? { type: config.type } : {}
    case 'Range': {
      const next: RuleConfig = {}
      if (getRangeMode(config) === 'text') {
        if (config.minLength != null) next.minLength = config.minLength
        if (config.maxLength != null) next.maxLength = config.maxLength
      } else {
        if (config.min != null) next.min = config.min
        if (config.max != null) next.max = config.max
      }
      return next
    }
    case 'Regex': {
      const next: RuleConfig = { pattern: config.pattern ?? '' }
      if (config.ignoreCase != null) next.ignoreCase = config.ignoreCase
      return next
    }
    case 'AllowedValues': {
      const next: RuleConfig = { values: config.values ?? [] }
      if (config.caseInsensitive != null) next.caseInsensitive = config.caseInsensitive
      return next
    }
    default:
      return {}
  }
}

export function formatConfigSummary(entry: RuleEntry): string {
  const config = entry.config
  switch (entry.ruleType) {
    case 'RequiredNonNull':
      return entry.config?.removeNullRows ? 'Remove null rows' : '—'
    case 'DataType':
      return config?.type ? `type: ${config.type}` : '—'
    case 'Range': {
      if (getRangeMode(config) === 'text') {
        return `length ${config?.minLength ?? '–'} to ${config?.maxLength ?? '–'}`
      }
      return `${config?.min ?? '–'} to ${config?.max ?? '–'}`
    }
    case 'Regex':
      return config?.pattern ? `/${config.pattern}/${config.ignoreCase ? 'i' : ''}` : '—'
    case 'AllowedValues': {
      const values = config?.values ?? []
      const label = values.join(', ')
      return values.length ? `${label}${config?.caseInsensitive ? ' (ci)' : ''}` : '—'
    }
    default:
      return '—'
  }
}

// Client-side validation mirroring the server rules, so we can flag issues
// before the round-trip. Returns index-prefixed messages like the API does.
export function validateEntry(entry: RuleEntry, index: number): string[] {
  const errors: string[] = []
  const prefix = `Rule[${index}]`

  if (!entry.columnName.trim()) errors.push(`${prefix}: Column name is required.`)
  if (!SEVERITIES.includes(entry.severity)) errors.push(`${prefix}: Severity must be one of: Error, Warning.`)

  const config = entry.config
  switch (entry.ruleType) {
    case 'DataType':
      if (!config?.type || !DATA_TYPES.includes(config.type)) {
        errors.push(`${prefix}: DataType rule requires "type" to be one of: date, decimal, int, guid, bool.`)
      }
      break
    case 'Range': {
      const hasAny =
        config?.min != null || config?.max != null || config?.minLength != null || config?.maxLength != null
      if (!hasAny) errors.push(`${prefix}: Range rule requires at least one of min, max, minLength, maxLength.`)
      if (config?.minLength != null && config.minLength < 0)
        errors.push(`${prefix}: minLength must be a non-negative integer.`)
      if (config?.maxLength != null && config.maxLength < 0)
        errors.push(`${prefix}: maxLength must be a non-negative integer.`)
      break
    }
    case 'Regex':
      if (!config?.pattern?.trim()) {
        errors.push(`${prefix}: Regex rule requires a non-empty pattern.`)
      } else {
        try {
          RegExp(config.pattern)
        } catch {
          errors.push(`${prefix}: Regex pattern does not compile.`)
        }
      }
      break
    case 'AllowedValues':
      if (!config?.values?.length) errors.push(`${prefix}: AllowedValues rule requires a non-empty values array.`)
      break
    default:
      break
  }

  return errors
}

// Identity of an entry for duplicate detection: same column + same rule type.
// Multiple rules on one column are allowed (e.g. RequiredNonNull + DataType).
export function entryKey(columnName: string, ruleType: RuleType) {
  return `${columnName.trim().toLowerCase()}::${ruleType}`
}

export function severityCounts(rules: RuleEntry[]): { error: number; warning: number } {
  return rules.reduce(
    (acc, rule) => {
      if (rule.severity === 'Error') acc.error += 1
      else if (rule.severity === 'Warning') acc.warning += 1
      return acc
    },
    { error: 0, warning: 0 },
  )
}
